import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { createCachedObservable } from '../utils/cached-observable.util';
import type {
  FamiliaArticulo,
  FamiliaArticuloCompleta,
  FamiliaWritePayload,
  FamiliasPaginatedResponse,
} from '../models/familia-articulo.model';

/** Extrae un array de filas desde distintas formas de respuesta del API. */
function extractFamiliasArray(body: unknown): unknown[] {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  const d = o['data'];
  if (Array.isArray(d)) return d;
  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    const inner = (d as Record<string, unknown>)['data'];
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

function mapRowToFamilia(raw: unknown): FamiliaArticulo | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const idRaw = r['id_familia'] ?? r['id'];
  const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
  if (!Number.isFinite(id)) return null;
  const nombre = String(r['nombre'] ?? r['name'] ?? '').trim();
  const codigo = r['codigo'];
  return {
    id_familia: id,
    nombre: nombre || `Familia #${id}`,
    codigo: typeof codigo === 'string' ? codigo : undefined,
  };
}

function mapRowToFamiliaCompleta(raw: unknown): FamiliaArticuloCompleta | null {
  const base = mapRowToFamilia(raw);
  if (!base || raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const idParentRaw = r['id_parent'];
  let id_parent: number | null = null;
  if (idParentRaw !== undefined && idParentRaw !== null) {
    const n = typeof idParentRaw === 'number' ? idParentRaw : Number(idParentRaw);
    id_parent = Number.isFinite(n) ? n : null;
  }
  const aa = r['articulos_activos'];
  let articulos_activos: number | undefined;
  if (typeof aa === 'number' && Number.isFinite(aa)) {
    articulos_activos = aa;
  } else if (aa != null) {
    const n = Number(aa);
    if (Number.isFinite(n)) articulos_activos = n;
  }
  const createdRaw = r['created_at'] ?? r['fecha_creacion'];
  const updatedRaw = r['updated_at'] ?? r['fecha_actualizacion'];
  const created_at = typeof createdRaw === 'string' && createdRaw.trim() ? createdRaw : undefined;
  const updated_at = typeof updatedRaw === 'string' && updatedRaw.trim() ? updatedRaw : undefined;
  return { ...base, id_parent, articulos_activos, created_at, updated_at };
}

function normalizeFamiliasResponse(body: unknown): FamiliaArticulo[] {
  const rows = extractFamiliasArray(body);
  const out: FamiliaArticulo[] = [];
  for (const row of rows) {
    const f = mapRowToFamilia(row);
    if (f) out.push(f);
  }
  return out;
}

function unwrapFamiliaCompletaResponse(res: unknown): FamiliaArticuloCompleta {
  const raw =
    res != null && typeof res === 'object' && 'data' in res && (res as { data: unknown }).data != null
      ? (res as { data: unknown }).data
      : res;
  const f = mapRowToFamiliaCompleta(raw);
  if (!f) {
    throw new Error('Respuesta de familia inválida');
  }
  return f;
}

export interface FamiliasQueryParams {
  page?: number;
  per_page?: number;
}

const FETCH_ALL_PAGE_SIZE = 100;

@Injectable({ providedIn: 'root' })
export class FamiliasArticuloService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api`;

  private readonly familiasBasicoCache = createCachedObservable(() =>
    this.http
      .get<unknown>(`${this.baseUrl}/familias/basico`)
      .pipe(map(normalizeFamiliasResponse)),
  );

  /**
   * Listado mínimo de familias (desplegable de artículos, etc.).
   * GET /api/familias/basico (caché en memoria).
   * Normaliza: array en raíz, `{ data: [...] }` o paginador Laravel.
   */
  getFamilias(): Observable<FamiliaArticulo[]> {
    return this.familiasBasicoCache.get();
  }

  clearFamiliasBasicoCache(): void {
    this.familiasBasicoCache.clear();
  }

  /**
   * Listado completo paginado (pantalla de configuración de familias).
   * GET /api/familias
   */
  getFamiliasPaginadas(params: FamiliasQueryParams = {}): Observable<FamiliasPaginatedResponse> {
    let httpParams = new HttpParams();
    if (params.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params.per_page != null) httpParams = httpParams.set('per_page', String(params.per_page));
    return this.http.get<FamiliasPaginatedResponse>(`${this.baseUrl}/familias`, { params: httpParams }).pipe(
      map((res) => ({
        meta: res.meta,
        data: (res.data ?? [])
          .map((row) => mapRowToFamiliaCompleta(row))
          .filter((x): x is FamiliaArticuloCompleta => x != null),
      })),
    );
  }

  /**
   * Todas las filas completas (GET /api/familias), acumulando páginas hasta vaciar el paginador.
   */
  getTodasLasFamiliasCompletas(): Observable<FamiliaArticuloCompleta[]> {
    return this.loadFamiliasCompletasAccumulated(1, []);
  }

  /** Detalle. GET /api/familias/:id */
  getFamilia(id: number): Observable<FamiliaArticuloCompleta> {
    return this.http
      .get<unknown>(`${this.baseUrl}/familias/${id}`)
      .pipe(map((body) => unwrapFamiliaCompletaResponse(body)));
  }

  /** Alta. POST /api/familias */
  createFamilia(payload: FamiliaWritePayload): Observable<FamiliaArticuloCompleta> {
    return this.http
      .post<unknown>(`${this.baseUrl}/familias`, payload)
      .pipe(map((res) => unwrapFamiliaCompletaResponse(res)));
  }

  /** Actualización. PUT /api/familias/:id */
  updateFamilia(id: number, payload: FamiliaWritePayload): Observable<FamiliaArticuloCompleta> {
    return this.http
      .put<unknown>(`${this.baseUrl}/familias/${id}`, payload)
      .pipe(map((res) => unwrapFamiliaCompletaResponse(res)));
  }

  /** Baja. DELETE /api/familias/:id */
  deleteFamilia(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/familias/${id}`);
  }

  private loadFamiliasCompletasAccumulated(
    page: number,
    acc: FamiliaArticuloCompleta[],
  ): Observable<FamiliaArticuloCompleta[]> {
    return this.getFamiliasPaginadas({ page, per_page: FETCH_ALL_PAGE_SIZE }).pipe(
      switchMap((res) => {
        const chunk = res.data ?? [];
        const next = [...acc, ...chunk];
        const lastPage = res.meta?.last_page ?? 1;
        if (page >= lastPage || chunk.length === 0) {
          return of(next);
        }
        return this.loadFamiliasCompletasAccumulated(page + 1, next);
      }),
    );
  }
}
